import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Alert, App, Button, Divider, Form, Input, Radio } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { CopyOutlined, ExportOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { SIGNUP_ENABLED, SUPPORT_URL } from 'constants/app-info';
import {
  ADMIN_ACCESS_STEPS,
  AUTH_FORM_PROPS,
  AuthLayout,
  AuthSteps,
  AuthSwitch,
  authStyles,
  emailRules,
  normalizeEmail,
  ON_SUBMIT,
  VALIDATION,
} from 'layouts/auth';
import type { AuthStepItem } from 'layouts/auth';
import { paths } from 'routes/routes';
import { AccessRequest, ROLE_LABELS, composeAccessMessage, copyText } from './compose-message';
import styles from './styles.module.scss';

/**
 * /request-access — phương án B của đặc tả §4: nói rõ cách được cấp quyền Admin/MOD và giúp
 * soạn sẵn lời nhắn gửi Admin. Trang không gọi API nào.
 */

const NAME_MAX = 64;

/**
 * Bật đăng ký trên web thì bước 1 thêm đường tạo tài khoản ngay tại đây. Là hàm (gọi lúc render)
 * chứ không phải hằng: routes/routes.tsx import trang này, nên đọc `paths` ngay lúc nạp module
 * sẽ rơi vào vòng import và làm sập cả app.
 */
const buildSteps = (): AuthStepItem[] =>
  SIGNUP_ENABLED
    ? [
        {
          ...ADMIN_ACCESS_STEPS[0],
          description: (
            <>
              Dùng email bạn đang đăng nhập app HP Media. Chưa có tài khoản?{' '}
              <Link to={paths.register}>Tạo tài khoản tại đây</Link>, đăng ký trong app, hoặc nhờ
              Admin tạo trực tiếp.
            </>
          ),
        },
        ...ADMIN_ACCESS_STEPS.slice(1),
      ]
    : ADMIN_ACCESS_STEPS;

const SUBTITLE = SIGNUP_ENABLED
  ? 'Trang quản trị chỉ mở cho tài khoản được Admin cấp vai trò Admin hoặc MOD. Tài khoản mới tạo cũng cần Admin cấp quyền trước.'
  : 'Trang quản trị chỉ mở cho tài khoản được Admin cấp vai trò Admin hoặc MOD, không có đăng ký tự do.';

const RequestAccessPage = () => {
  const { message } = App.useApp();
  const location = useLocation();
  const [form] = Form.useForm<AccessRequest>();
  const [initialEmail] = useState(() =>
    normalizeEmail((location.state as { email?: string } | null)?.email)
  );
  // Lời nhắn hiện ra để tự chép khi trình duyệt không cho ghi clipboard.
  const [manualText, setManualText] = useState<string | null>(null);
  const manualRef = useRef<TextAreaRef>(null);

  useEffect(() => {
    if (!manualText) return;
    const el = manualRef.current?.resizableTextArea?.textArea;
    el?.focus();
    el?.select();
  }, [manualText]);

  const onFinish = async (values: AccessRequest) => {
    const text = composeAccessMessage({ ...values, email: normalizeEmail(values.email) });
    if (await copyText(text)) {
      setManualText(null);
      message.success('Đã sao chép. Dán vào tin nhắn gửi Admin.');
    } else {
      setManualText(text);
    }
  };

  return (
    <AuthLayout
      title="Yêu cầu quyền truy cập"
      subtitle={SUBTITLE}
      backTo={{ to: paths.login, label: 'Quay lại đăng nhập' }}
      cardFooter={<AuthSwitch prompt="Đã có quyền?" to={paths.login} label="Đăng nhập" />}
    >
      <AuthSteps items={buildSteps()} />

      <Divider className={styles.divider} />

      <section aria-labelledby="compose-title">
        <h2 id="compose-title" className={styles.sectionTitle}>
          Soạn lời nhắn gửi Admin
        </h2>
        <p className={styles.sectionLead}>
          Không bắt buộc. Điền ba ô để có sẵn lời nhắn, rồi dán vào tin nhắn gửi Admin.
        </p>

        <Form<AccessRequest>
          {...AUTH_FORM_PROPS}
          form={form}
          name="request-access"
          initialValues={{ fullName: '', email: initialEmail, role: 'MOD' }}
          onFinish={onFinish}
          onValuesChange={(changed) => {
            if (manualText) setManualText(null);
            // Ô đang báo lỗi thì kiểm tra lại ngay khi gõ, để lỗi biến mất lúc đã sửa đúng.
            const invalid = (Object.keys(changed) as (keyof AccessRequest)[]).filter(
              (name) => form.getFieldError(name).length > 0
            );
            if (invalid.length) form.validateFields(invalid).catch(() => undefined);
          }}
        >
          <Form.Item
            name="fullName"
            label="Họ tên"
            validateFirst
            validateTrigger="onBlur"
            rules={[
              // Chỉ báo "bắt buộc" khi bấm Sao chép, không báo lúc con trỏ rời ô trống.
              {
                required: true,
                whitespace: true,
                message: VALIDATION.nameRequired,
                validateTrigger: ON_SUBMIT,
              },
              { max: NAME_MAX, message: `Họ tên tối đa ${NAME_MAX} ký tự.` },
            ]}
          >
            <Input
              autoComplete="name"
              placeholder="Nhập họ tên của bạn"
              prefix={<UserOutlined aria-hidden="true" />}
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email tài khoản"
            rules={emailRules}
            validateFirst
            validateTrigger="onBlur"
            normalize={normalizeEmail}
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Email bạn dùng cho app HP Media"
              prefix={<MailOutlined aria-hidden="true" />}
            />
          </Form.Item>

          <Form.Item name="role" label="Vai trò cần cấp">
            <Radio.Group optionType="button" buttonStyle="solid" block>
              <Radio.Button value="MOD">{ROLE_LABELS.MOD}</Radio.Button>
              <Radio.Button value="ADMIN">{ROLE_LABELS.ADMIN}</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Button htmlType="submit" size="large" block icon={<CopyOutlined aria-hidden="true" />}>
            Sao chép lời nhắn
          </Button>

          {manualText && (
            <div className={styles.manual}>
              <Alert
                type="warning"
                showIcon
                message="Trình duyệt không cho sao chép tự động. Chọn đoạn dưới đây rồi chép (Ctrl+C)."
              />
              <label htmlFor="request-access-message" className={styles.manualLabel}>
                Lời nhắn
              </label>
              <Input.TextArea
                id="request-access-message"
                ref={manualRef}
                value={manualText}
                readOnly
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            </div>
          )}

          <p className={`${authStyles.hint} ${styles.privacy}`}>
            Thông tin chỉ nằm trên máy bạn, trang này không gửi đi đâu.
          </p>
        </Form>
      </section>

      {SUPPORT_URL && (
        <Button
          type="primary"
          size="large"
          block
          href={SUPPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
          icon={<ExportOutlined aria-hidden="true" />}
          iconPosition="end"
          className={styles.contact}
        >
          Liên hệ Admin
          <span className={styles.srOnly}> (mở trong thẻ mới)</span>
        </Button>
      )}
    </AuthLayout>
  );
};

export default RequestAccessPage;
